/**
 * LedgerJournal — журнал операций по счёту с постраничностью.
 *
 * В каждой строке показываем остаток «стало». Журнал открывают, когда
 * сумма не сошлась, и там важно не само движение, а на чём баланс
 * свернул не туда — без «стало» это приходилось бы складывать в уме.
 */

import {type LedgerEntry} from '@services/ledger';
import {Button} from '@components/ui/Button';
import {cn} from '@lib/utils';
import {formatTiyin} from '@utils/format';

import {SectionCard} from '../SectionCard';

import {fmtLedgerDate, periodLabel, signedAmount, sourceLabel} from './LedgerJournal.utils';

type Props = {
  list: LedgerEntry[];
  totalCount: number;
  page: number;
  pages: number;
  onPage: (next: number) => void;
};

export const LedgerJournal = ({list, totalCount, page, pages, onPage}: Props) => (
  <SectionCard
    title="Журнал операций"
    hint={totalCount > 0 ? `Всего операций: ${totalCount}` : undefined}
  >
    {list.length === 0 ? (
      <div className="py-4 text-center text-[13px] text-muted-foreground">
        Операций по счёту ещё не было
      </div>
    ) : (
      <ul className="flex flex-col">
        {list.map((e) => (
          <Entry key={e.id} entry={e} />
        ))}
      </ul>
    )}

    {pages > 1 && (
      <div className="mt-3 flex items-center justify-between border-t border-skr-hairline pt-3">
        <Button
          variant="outline"
          className="h-9 rounded-[9px]"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Назад
        </Button>
        <span className="text-[12.5px] text-muted-foreground">
          {page} из {pages}
        </span>
        <Button
          variant="outline"
          className="h-9 rounded-[9px]"
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
        >
          Дальше
        </Button>
      </div>
    )}
  </SectionCard>
);

/** Строка журнала — приватный под-элемент этого экрана. */
const Entry = ({entry}: {entry: LedgerEntry}) => {
  const period = periodLabel(entry);
  return (
    <li className="flex items-start justify-between gap-3 border-t border-skr-hairline py-2.5 first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <div className="text-[13px] font-medium">{sourceLabel(entry.source)}</div>
        <div className="mt-0.5 text-[12px] leading-[1.45] text-muted-foreground">
          {entry.description || '—'}
        </div>
        <div className="mt-0.5 text-[11.5px] text-skr-faint">
          {fmtLedgerDate(entry.createdAt)}
          {period !== null && ` · период ${period}`}
        </div>
      </div>
      <div className="shrink-0 text-right font-mono">
        <div
          className={cn(
            'text-[13.5px] font-semibold',
            entry.kind === 'credit' ? 'text-skr-paid' : 'text-foreground',
          )}
        >
          {signedAmount(entry)}
        </div>
        <div className="mt-0.5 text-[11.5px] text-skr-faint">
          стало {formatTiyin(entry.balanceAfterTiyin)}
        </div>
      </div>
    </li>
  );
};
